import { useRef, useEffect, useCallback } from 'react';

const OTP_LENGTH = 4;

/** Normalize any input (typing, paste, SMS autofill) to up to 4 digits. */
export function normalizeOtpValue(raw) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, OTP_LENGTH);
}

/**
 * OTP field optimized for iOS/Android SMS autofill:
 * - Single transparent input with autocomplete="one-time-code" (receives full code)
 * - Four visual digit boxes
 * - Optional onComplete when 4 digits are entered (e.g. auto-submit)
 */
const OtpInput = ({
  value = '',
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  length = OTP_LENGTH,
  boxClassName = 'w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none transition-colors',
  className = 'flex justify-center gap-2',
}) => {
  const hiddenRef = useRef(null);
  const completedRef = useRef('');
  const code = normalizeOtpValue(value);
  const digits = Array.from({ length }, (_, i) => code[i] ?? '');

  const applyValue = useCallback(
    (raw) => {
      const next = normalizeOtpValue(raw).slice(0, length);
      if (next !== value) onChange?.(next);
      return next;
    },
    [length, onChange, value]
  );

  useEffect(() => {
    if (!autoFocus || disabled) return;
    const t = setTimeout(() => hiddenRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, [autoFocus, disabled]);

  useEffect(() => {
    if (disabled || value.length !== length) {
      if (value.length < length) completedRef.current = '';
      return;
    }
    if (completedRef.current === value) return;
    completedRef.current = value;
    onComplete?.(value);
  }, [value, length, disabled, onComplete]);

  const handleHiddenChange = (e) => {
    applyValue(e.target.value);
  };

  const handleHiddenInput = (e) => {
    const next = applyValue(e.target.value);
    if (e.target.value !== next) e.target.value = next;
  };

  return (
    <div className={`relative ${className}`} dir="ltr">
      <input
        ref={hiddenRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        name="one-time-code"
        enterKeyHint="done"
        maxLength={length}
        value={normalizeOtpValue(value)}
        disabled={disabled}
        onChange={handleHiddenChange}
        onInput={handleHiddenInput}
        aria-label="كود التحقق"
        className="absolute inset-0 z-10 w-full h-full min-h-[3.5rem] cursor-text opacity-0"
        style={{ caretColor: 'transparent' }}
      />
      <div className="pointer-events-none flex justify-center gap-2" aria-hidden="true">
        {Array.from({ length }, (_, i) => {
          const d = digits[i]?.trim() ?? '';
          const filled = !!d;
          return (
            <div
              key={i}
              className={`${boxClassName} flex items-center justify-center bg-white ${
                filled ? 'border-primary text-slate-900' : 'text-slate-400'
              }`}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OtpInput;
